import React from 'react';
import { Calendar, Briefcase, ChevronRight, CheckCircle, Circle } from 'lucide-react';
import { WorkOrder } from '../../types/workOrders';
import { format, parse } from 'date-fns';
import { getAllActiveTasks } from '../../utils/firebase';
import { useAuthStore } from '../../store/authStore';

interface WorkOrderCardProps {
  workOrder: WorkOrder;
  onClick: () => void;
  isCompleted?: boolean;
}

export function WorkOrderCard({ workOrder, onClick, isCompleted = false }: WorkOrderCardProps) {
  const { workerId } = useAuthStore();
  const [hasActiveTasks, setHasActiveTasks] = React.useState(false);

  React.useEffect(() => {
    const checkActiveTasks = async () => {
      if (!workerId) return;
      try {
        const activeTasks = await getAllActiveTasks(workerId);
        const hasActive = activeTasks.some(task => 
          workOrder.Tasks.some(orderTask => orderTask.id === task.task_id)
        );
        setHasActiveTasks(hasActive);
      } catch (error) {
        console.error('Error checking active tasks:', error);
      }
    };

    if (!isCompleted) {
      checkActiveTasks();
    }
  }, [workerId, workOrder.Tasks, isCompleted]);
  
  const formatDate = (dateStr: string) => {
    try {
      const date = parse(dateStr, 'yyyyMMdd', new Date());
      return format(date, 'MMM d, yyyy');
    } catch {
      return 'No due date';
    }
  };

  const visibleTasks = workOrder.Tasks.filter(t => t.isVisible);
  const completedTasks = visibleTasks.filter(t => t.isCompleted);
  const incompleteTasks = visibleTasks.filter(t => !t.isCompleted);

  const getStatusIndicator = () => {
    if (hasActiveTasks) {
      return <div className="h-3 w-3 rounded-full bg-blue-500 animate-pulse" />;
    }
    if (completedTasks.length > 0 && incompleteTasks.length > 0) {
      return <div className="h-3 w-3 rounded-full bg-yellow-500" />;
    }
    if (completedTasks.length === visibleTasks.length) {
      return <div className="h-3 w-3 rounded-full bg-green-500" />;
    }
    return null;
  };

  return (
    <div
      onClick={onClick}
      className={`card w-full p-6 text-left hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer ${
        isCompleted ? 'bg-gray-50 dark:bg-gray-900/20' : ''
      }`}
    >
      <div className="flex justify-between items-start">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">WO #{workOrder.wo_number}</h3>
            {!isCompleted && getStatusIndicator()}
          </div>
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            <span className="text-sm text-gray-600 dark:text-gray-300">{workOrder.work_type_alias}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <Calendar className="h-4 w-4" />
            <span className="text-sm">Due: {formatDate(workOrder.date_due)}</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
              <CheckCircle className="h-4 w-4" />
              <span>{completedTasks.length} completed</span>
            </div>
            {!isCompleted && (
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <Circle className="h-4 w-4" />
                <span>{incompleteTasks.length} pending</span>
              </div>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>
    </div>
  );
}